import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { v4 } from "uuid";
import { cloneDeep, debounce } from "lodash";
import { AxiosResponse, AxiosError } from "axios";
import { Button } from "primereact/button";
import { ConfirmDialog } from "primereact/confirmdialog";
import { Toast } from "primereact/toast";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ProgressSpinner } from "primereact/progressspinner";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { FilterMatchMode } from "primereact/api";
import { ButtonInput } from "../../components/ButtonInput/ButtonInput";
import { DateInput } from "../../components/DateInput/DateInput";
import { DropdownInput } from "../../components/DropdownInput/DropdownInput";
import { PdfPreview } from "../../components/PdfPreview/PDFPreview";
import { MercantileType } from "../../models/ParamSettings";
import {
  DocumentOfReceive,
  DocumentShort,
  ReceiveStatus,
} from "../../models/DocumentOfReceive";
import { Product, ProductType, ProductCategory } from "../../models/Product";
import { Warehouse } from "../../models/Warehouse";
import { RoleType } from "../../models/Role";
import { Utility } from "../../core/Utility";
import { NewInvoiceIdForm } from "../../forms/InvoiceIdForm/InvoiceIdForm";
import { getPreviewMercantileNewInputTemplate } from "../../document-templates/PreviewMercantileNewInputTemplate";
import { PageLayout } from "../../layout/PageLayout/PageLayout";
import { useApplicationContext } from "../../application/ApplicationContext";
import { srpsCalculation } from "../../services/SprsService";
import { activeUser } from "../../services/AuthService";
import { getAllWarehouses } from "../../services/WarehouseService";
import { getAllProducts } from "../../services/ProductService";
import { getAllProductCategories } from "../../services/ProductCategoryService";
import { getAllSettings } from "../../services/SettingsService";
import {
  getAllDocuments,
  getOneDocument,
  deleteOneDocument,
} from "../../services/NewVersionService";
import "./NewMercantileOutputPage.scss";

export const NewMercantileOutputPage = () => {
  const {
    //load and set global filters
    state,
    selectedWarehouseSet,
    selectedDateFromSet,
    selectedDateToSet,
    selectedProductCategorySet,
    selectedProductSet,
  } = useApplicationContext();

  const { t } = useTranslation();

  const businessYear = state.businessYear ?? new Date().getFullYear();

  const yearStart = useMemo(
    () => new Date(businessYear, 0, 1, 0, 0, 1),
    [businessYear],
  );
  const yearEnd = useMemo(
    () => new Date(businessYear, 11, 31, 23, 59, 59),
    [businessYear],
  );

  const getDefaultRangeForBusinessYear = () => {
    const today = new Date();
    const currentYear = today.getFullYear();
    if (businessYear === currentYear) {
      const oneDayAgo = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate() - 1,
        0,
        0,
        1,
      );
      const from = Utility.clampDateToRange(oneDayAgo, yearStart, yearEnd);
      const to = Utility.clampDateToRange(today, yearStart, yearEnd);
      return { from, to };
    }
    // za druge godine – ceo opseg te poslovne godine
    return { from: yearStart, to: yearEnd };
  };

  const [dateFrom, setDateFrom] = useState<Date>(() => {
    if (state.selectedDateFrom) {
      return Utility.clampDateToRange(
        new Date(state.selectedDateFrom),
        yearStart,
        yearEnd,
      );
    }
    return getDefaultRangeForBusinessYear().from;
  });

  const [dateTo, setDateTo] = useState<Date>(() => {
    if (state.selectedDateTo) {
      return Utility.clampDateToRange(
        new Date(state.selectedDateTo),
        yearStart,
        yearEnd,
      );
    }
    return getDefaultRangeForBusinessYear().to;
  });

  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [productCategories, setProductCategories] = useState<ProductCategory[]>(
    [],
  );
  const [settings, setSettings] = useState<any[]>([]);

  const [selectedMercantileOutput, setSelectedMercantileOutput] =
    useState<DocumentOfReceive>();
  const [showForm, setShowForm] = useState<string>("");

  const [selectedWarehouse, setSelectedWarehouse] = useState<Warehouse>(
    activeUser?.role?.name === RoleType.Warehouseman
      ? (activeUser?.warehouse as Warehouse)
      : ({} as Warehouse),
  );
  const [selectedProductCategory, setSelectedProductCategory] =
    useState<ProductCategory>();
  const [selectedProduct, setSelectedProduct] = useState<Product>(
    {} as Product,
  );

  const [allMercantileOutputDocuments, setAllMercantileOutputDocuments] =
    useState<DocumentShort[]>([]);
  const [mercantileOutputDocuments, setMercantileOutputDocuments] = useState<
    DocumentShort[]
  >([]);
  const [nettoSum, setNettoSum] = useState<number>(0);
  const [srpsSum, setSrpsSum] = useState<number>(0);
  const [filteredOutputDocuments, setFilteredOutputDocuments] = useState<
    DocumentShort[]
  >([]);

  const [deleteConfirmVisible, setDeleteConfirmVisible] =
    useState<boolean>(false);
  const [outputToDelete, setOutputToDelete] = useState<string>("");

  const [processing, setProcessing] = useState(false);
  const [globalFilterValue, setGlobalFilterValue] = useState<string>("");
  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    finishedDocumentNumber: {
      value: null,
      matchMode: FilterMatchMode.CONTAINS,
    },
    documentPairId: { value: null, matchMode: FilterMatchMode.CONTAINS },
    loadingCityName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    partnerName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    brutto: { value: null, matchMode: FilterMatchMode.CONTAINS },
    tara: { value: null, matchMode: FilterMatchMode.CONTAINS },
    netto: { value: null, matchMode: FilterMatchMode.CONTAINS },
    srps: { value: null, matchMode: FilterMatchMode.CONTAINS },
    moisture: { value: null, matchMode: FilterMatchMode.CONTAINS },
    impurity: { value: null, matchMode: FilterMatchMode.CONTAINS },
    breakage: { value: null, matchMode: FilterMatchMode.CONTAINS },
    defect: { value: null, matchMode: FilterMatchMode.CONTAINS },
    hectoliter: { value: null, matchMode: FilterMatchMode.CONTAINS },
    driverName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    vehicleNumber: { value: null, matchMode: FilterMatchMode.CONTAINS },
    status: { value: null, matchMode: FilterMatchMode.EQUALS },
  });

  const debouncedSetFilters = useMemo(
    () =>
      debounce((nextFilters: any) => {
        setFilters(nextFilters);
      }, 300),
    [],
  );

  const debouncedSetFilteredOutputDocuments = useMemo(
    () =>
      debounce((filteredData: DocumentShort[]) => {
        setFilteredOutputDocuments(filteredData);
      }, 300),
    [],
  );

  useEffect(() => {
    return () => {
      debouncedSetFilters.cancel();
      debouncedSetFilteredOutputDocuments.cancel();
    };
  }, [debouncedSetFilters, debouncedSetFilteredOutputDocuments]);

  const [pdfData, setPdfData] = useState<any>(
    getPreviewMercantileNewInputTemplate(
      filteredOutputDocuments,
      selectedWarehouse?.name,
      selectedProduct?.name,
      selectedProductCategory?.name ?? "",
      dateFrom,
      dateTo,
      nettoSum ?? 0,
      srpsSum ?? 0,
      false,
    ),
  );

  const downloadPdfDocument = useRef(null);
  const printPdfDocument = useRef(null);

  useEffect(() => {
    const unwrapArray = (resp: any): any[] => {
      const d = resp?.data;
      if (d?.result && Array.isArray(d.result)) return d.result;
      if (Array.isArray(d)) return d;
      return [];
    };

    (async () => {
      try {
        setProcessing(true);

        const [whResp, prodResp, catResp, setResp] = await Promise.all([
          getAllWarehouses(),
          getAllProducts(),
          getAllProductCategories(),
          getAllSettings(),
        ]);

        setWarehouses(unwrapArray(whResp));
        setProducts(unwrapArray(prodResp));
        setProductCategories(unwrapArray(catResp));
        setSettings(unwrapArray(setResp));

        if (activeUser?.role?.name === RoleType.Warehouseman) {
          selectedWarehouseSet(activeUser?.warehouse as Warehouse);
        } else {
          if (state.selectedWarehouse)
            setSelectedWarehouse(state.selectedWarehouse);
        }
        if (state.selectedProductCategory)
          setSelectedProductCategory(state.selectedProductCategory);
        if (state.selectedProduct) setSelectedProduct(state.selectedProduct);

        const isWarehouseman = activeUser?.role?.name === RoleType.Warehouseman;
        const warehouseId = activeUser?.warehouse?.id;
        const from = dateFrom;
        const to = new Date(dateTo.getTime() + 24 * 60 * 60 * 1000);

        const response: AxiosResponse = await getAllDocuments(
          true, // mercantile
          false, // outputs
          from,
          to,
          isWarehouseman,
          isWarehouseman ? warehouseId : undefined,
        );

        const allDocuments = response?.data?.result ?? [];
        setAllMercantileOutputDocuments(allDocuments);

        const filteredByInitialState: DocumentShort[] = [];
        for (const doc of allDocuments) {
          if (
            (state?.selectedProductCategory?.id
              ? doc.categoryId == state.selectedProductCategory.id
              : true) &&
            (state?.selectedProduct?.id
              ? doc.productId == state.selectedProduct.id
              : true) &&
            (state?.selectedWarehouse?.id
              ? doc.warehouseName == state.selectedWarehouse.name
              : true)
          ) {
            filteredByInitialState.push(doc);
          }
        }

        setFilteredOutputDocuments(filteredByInitialState);
        formatMercantileDocumentsData(filteredByInitialState);
      } catch (e) {
        console.log(e as AxiosError);
      } finally {
        setProcessing(false);
      }
    })();
  }, []);

  const toast = useRef(null);
  const navigate = useNavigate();

  const resetFilters = () => {
    //reset all other filters
    setFilters({
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
      finishedDocumentNumber: {
        value: null,
        matchMode: FilterMatchMode.CONTAINS,
      },
      documentPairId: { value: null, matchMode: FilterMatchMode.CONTAINS },
      loadingCityName: { value: null, matchMode: FilterMatchMode.CONTAINS },
      partnerName: { value: null, matchMode: FilterMatchMode.CONTAINS },
      brutto: { value: null, matchMode: FilterMatchMode.CONTAINS },
      tara: { value: null, matchMode: FilterMatchMode.CONTAINS },
      netto: { value: null, matchMode: FilterMatchMode.CONTAINS },
      srps: { value: null, matchMode: FilterMatchMode.CONTAINS },
      moisture: { value: null, matchMode: FilterMatchMode.CONTAINS },
      impurity: { value: null, matchMode: FilterMatchMode.CONTAINS },
      breakage: { value: null, matchMode: FilterMatchMode.CONTAINS },
      defect: { value: null, matchMode: FilterMatchMode.CONTAINS },
      hectoliter: { value: null, matchMode: FilterMatchMode.CONTAINS },
      driverName: { value: null, matchMode: FilterMatchMode.CONTAINS },
      vehicleNumber: { value: null, matchMode: FilterMatchMode.CONTAINS },
      status: { value: null, matchMode: FilterMatchMode.EQUALS },
    });
    setGlobalFilterValue("");
  };

  useEffect(() => {
    formatMercantileDocumentsData(allMercantileOutputDocuments);
    resetFilters();
  }, [selectedWarehouse, selectedProduct, selectedProductCategory]);

  const formatPdfData = () => {
    // populate pdf when filter changes
    const newPdf = getPreviewMercantileNewInputTemplate(
      filteredOutputDocuments,
      selectedWarehouse.name,
      selectedProduct.name,
      selectedProductCategory?.name ?? "",
      dateFrom,
      dateTo,
      nettoSum ?? 0,
      srpsSum ?? 0,
      false,
    );
    setPdfData(newPdf);
  };

  useEffect(() => {
    setProcessing(true);
    const isWarehouseman = activeUser?.role?.name === RoleType.Warehouseman;
    const warehouseId = activeUser?.warehouse?.id;
    const range = getDefaultRangeForBusinessYear();

    const selectedFrom = state.selectedDateFrom
      ? new Date(state.selectedDateFrom)
      : null;
    const selectedTo = state.selectedDateTo
      ? new Date(state.selectedDateTo)
      : null;

    const isSameBusinessYear = (d: Date | null) =>
      !!d && d.getFullYear() === businessYear;

    const effectiveFrom = isSameBusinessYear(selectedFrom)
      ? Utility.clampDateToRange(selectedFrom as Date, yearStart, yearEnd)
      : range.from;

    const effectiveTo = isSameBusinessYear(selectedTo)
      ? Utility.clampDateToRange(selectedTo as Date, yearStart, yearEnd)
      : range.to;
    setDateFrom(effectiveFrom);
    setDateTo(effectiveTo);

    getAllDocuments(
      true, // mercantile
      false, // outputs
      effectiveFrom,
      new Date(effectiveTo.getTime() + 24 * 60 * 60 * 1000),
      isWarehouseman,
      isWarehouseman ? warehouseId : undefined,
    )
      .then((response: AxiosResponse) => {
        const allDocuments = response.data.result;
        const allFormatedDocuments: DocumentShort[] = [];
        for (const oneDocument of allDocuments) {
          if (
            new Date(oneDocument?.date).getTime() <
            effectiveTo.getTime() + 24 * 60 * 60 * 1000 - 1
          ) {
            allFormatedDocuments.push({
              ...oneDocument,
            });
          }
        }
        setAllMercantileOutputDocuments(allFormatedDocuments);
        if (activeUser?.role?.name !== RoleType.Warehouseman) {
          setSelectedWarehouse({} as Warehouse);
          selectedWarehouseSet(undefined);
        }
        setSelectedProductCategory(undefined);
        selectedProductCategorySet(undefined);
        setSelectedProduct({} as Product);
        selectedProductSet(undefined);
        setFilteredOutputDocuments(allFormatedDocuments);
        formatMercantileDocumentsData(allFormatedDocuments);
        resetFilters();
      })
      .finally(() => setProcessing(false));
  }, [state.selectedDateFrom, state.selectedDateTo, yearStart, yearEnd]);

  const formatMercantileDocumentsData = (allDocuments: DocumentShort[]) => {
    let copyOfMercantileDocuments: DocumentShort[] = [];
    let amountOfSrps: number = 0;
    let amountOfNetto: number = 0;

    for (const mercantileOutput of allDocuments) {
      //filtriranje otpremnica po magacinu, kategoriji i vrsti robe
      if (
        (selectedProductCategory?.id
          ? mercantileOutput.categoryId == selectedProductCategory.id
          : true) && //otpremnice za izabranu grupu robe (default za sve)...
        (selectedProduct?.id
          ? mercantileOutput.productId == selectedProduct.id
          : true) && //otpremnice za izabranu robu (default za sve)...
        (selectedWarehouse?.id
          ? mercantileOutput.warehouseName == selectedWarehouse?.name
          : true) // && //...u izabranom magacinu...
      ) {
        copyOfMercantileDocuments.push(mercantileOutput);
        if (mercantileOutput.netto) amountOfNetto += mercantileOutput.netto;
        if (mercantileOutput.srps) amountOfSrps += mercantileOutput.srps;
      }
    }
    copyOfMercantileDocuments.sort((a: DocumentShort, b: DocumentShort) => {
      return a.date < b.date ? 1 : -1;
    });
    setMercantileOutputDocuments(copyOfMercantileDocuments);
    setNettoSum(amountOfNetto);
    setSrpsSum(amountOfSrps);
  };

  useEffect(() => {
    let amountOfSrps: number = 0;
    let amountOfNetto: number = 0;
    for (const oneOutput of filteredOutputDocuments) {
      if (oneOutput.netto) amountOfNetto += oneOutput.netto;
      if (oneOutput.srps) amountOfSrps += oneOutput.srps;
    }
    setNettoSum(amountOfNetto);
    setSrpsSum(amountOfSrps);

    formatPdfData();
  }, [filteredOutputDocuments]);

  const handleWarehouseChange = (prop: string, value: any) => {
    let copyOfNewWarehouse = cloneDeep(selectedWarehouse);
    copyOfNewWarehouse = warehouses.find(
      (item) => item.id == value,
    ) as Warehouse;
    setSelectedWarehouse(copyOfNewWarehouse); //set local state
    selectedWarehouseSet(copyOfNewWarehouse); //set global state
  };

  const onDateFromChange = (_property: string, value: Date | null) => {
    if (!value) {
      const { from } = getDefaultRangeForBusinessYear();
      setDateFrom(from);
      selectedDateFromSet(from);
      return;
    }
    let d = Utility.clampDateToRange(value, yearStart, yearEnd);
    if (d > dateTo) {
      d = dateTo;
    }
    setDateFrom(d);
    selectedDateFromSet(d);
  };

  const onDateToChange = (_property: string, value: Date | null) => {
    if (!value) {
      const { to } = getDefaultRangeForBusinessYear();
      setDateTo(to);
      selectedDateToSet(to);
      return;
    }
    let d = Utility.clampDateToRange(value, yearStart, yearEnd);
    if (d < dateFrom) {
      d = dateFrom;
    }
    setDateTo(d);
    selectedDateToSet(d);
  };

  const handleProductCategoryChange = (prop: string, value: any) => {
    const copyOfNewProductCategory = productCategories.find(
      (item) => item.id == value,
    ) as ProductCategory;
    setSelectedProductCategory(copyOfNewProductCategory);
    selectedProductCategorySet(copyOfNewProductCategory);
    setSelectedProduct({} as Product);
    selectedProductSet({} as Product);
  };

  const handleProductChange = (prop: string, value: any) => {
    let copyOfNewProduct = cloneDeep(selectedProduct);
    copyOfNewProduct = products.find((item) => item.id == value) as Product;
    setSelectedProduct(copyOfNewProduct);
    selectedProductSet(copyOfNewProduct);
  };

  const onGlobalFilterChange = (e: any) => {
    const value = e.target.value;
    let _filters = { ...filters };

    _filters["global"].value = value;

    setFilters(_filters);
    setGlobalFilterValue(value);
  };

  const statusOptions = [
    { label: t("Stigao"), value: ReceiveStatus.Arrived },
    { label: t("Zaključena otpremnica"), value: ReceiveStatus.Finished },
    { label: t("Najavljen"), value: ReceiveStatus.NotArrived },
  ];

  const statusItemTemplate = (option: any) => {
    return (
      <div className="p-multiselect-representative-option">
        <span className="image-text">{option.label}</span>
      </div>
    );
  };

  const statusRowFilterTemplate = (options: any) => {
    return (
      <Dropdown
        value={options.value}
        options={statusOptions}
        onChange={(e) => {
          options.filterApplyCallback(e.value);
        }}
        itemTemplate={statusItemTemplate}
        placeholder="Izaberite"
        className="p-column-filter"
        showClear
      />
    );
  };

  const refreshDocuments = async () => {
    const isWarehouseman = activeUser?.role?.name === RoleType.Warehouseman;
    const warehouseId = activeUser?.warehouse?.id;
    const from = dateFrom;
    const to = new Date(dateTo.getTime() + 24 * 60 * 60 * 1000);

    setProcessing(true);
    try {
      const response: AxiosResponse = await getAllDocuments(
        true,
        false,
        from,
        to,
        isWarehouseman,
        isWarehouseman ? warehouseId : undefined,
      );

      const allDocuments = response?.data?.result ?? [];
      setAllMercantileOutputDocuments(allDocuments);
      setFilteredOutputDocuments(allDocuments);
      formatMercantileDocumentsData(allDocuments);
      resetFilters();
    } finally {
      setProcessing(false);
    }
  };

  const accept = async () => {
    try {
      setProcessing(true);
      await deleteOneDocument(outputToDelete);
      setOutputToDelete("");
      await refreshDocuments();

      //@ts-ignore
      toast.current.show({
        severity: "success",
        detail: t("Otpremnica je izbrisana"),
        life: 3000,
      });
    } finally {
      setProcessing(false);
    }
  };

  const {
    control,
    formState: { errors },
    setValue,
  } = useForm({
    mode: "all",
  });

  const renderHeader = () => {
    return (
      <div className="search-container">
        <span className="p-input-icon-left">
          <i className="pi pi-search" />
          <InputText
            value={globalFilterValue}
            onChange={onGlobalFilterChange}
            placeholder="Globalna pretraga..."
          />
        </span>
      </div>
    );
  };

  const formatFinishedDocumentId = (rowData: DocumentShort) => {
    return rowData.finishedDocumentNumber || rowData.finishedDocumentId || "/";
  };

  const formatProductName = (rowData: DocumentShort) => {
    return products?.find((oneProduct) => oneProduct.id == rowData.productId)
      ?.name;
  };

  const formatDate = (rowData: DocumentShort) => {
    return Utility.formatDate(new Date(rowData.date));
  };

  const formatStatus = (rowData: DocumentShort) => {
    const status = rowData.status;
    const isFinished = rowData.finishedDocument;
    if (status === ReceiveStatus.Arrived) {
      return <span style={{ color: "#EDBD12" }}>{t("Stigao")}</span>;
    }
    if (status === ReceiveStatus.Finished) {
      if (isFinished !== true) {
        return (
          <span style={{ color: "#00A553" }}>{t("Zaključena otpremnica")}</span>
        );
      } else {
        //za jednom zakljucenu pa ponovo otkljucanu otpremnicu
        return (
          <span style={{ color: "#DC3545" }}>{t("Zaključena otpremnica")}</span>
        );
      }
    }
    return <span style={{ color: "#DC3545" }}>{t("Najavljen")}</span>;
  };

  const amountNetto = (rowData: DocumentShort) => {
    return rowData?.netto ? Utility.addThousandSeparator(rowData.netto) : "";
  };

  const amountSrps = (rowData: DocumentShort) => {
    return rowData?.srps ? Utility.addThousandSeparator(rowData.srps) : "";
  };

  const formatControlHouseId = (rowData: DocumentShort) => {
    return (
      <div className="invoice-container">
        <Button
          id="buttonInputWithIconId"
          label={`${
            rowData?.controlHouseInvoiceId === null ||
            rowData?.controlHouseInvoiceId === ""
              ? "unesite"
              : rowData?.controlHouseInvoiceId
          }`}
          onClick={() => {
            getOneDocument(rowData.id).then((resp: any) => {
              const oneOutput = resp?.data?.result ?? resp?.data;
              if (oneOutput?.finishedDocument === true) {
                setSelectedMercantileOutput(cloneDeep(oneOutput));
                setShowForm(v4());
              } else {
                //@ts-ignore
                toast.current.show({
                  severity: "error",
                  detail: t(
                    "Nije moguće uneti broj računa transporta za otpremnice koje nisu zaključene",
                  ),
                  life: 3000,
                });
              }
            });
          }}
          style={{
            border: "1px solid #ccc",
            backgroundColor: "#eee",
            color: "#000",
          }}
        />
      </div>
    );
  };

  const formatOptions = (rowData: DocumentShort) => {
    return (
      <div className="action-buttons">
        <ButtonInput
          addStyle={{ border: "none", background: "#198754" }}
          labelText={""}
          icon="bi bi-eye"
          onButtonClick={() => {
            const id = rowData.id;
            navigate(`/document-of-output-preview-fast/${id}`);
          }}
        />
        {(activeUser?.role?.name === RoleType.Superadmin ||
          activeUser?.role?.name === RoleType.Administrator ||
          activeUser?.role?.name === RoleType.Warehouseman ||
          activeUser?.role?.name === RoleType.Director ||
          activeUser?.role?.name === RoleType.FinishingCenter) && (
          <ButtonInput
            addStyle={{ border: "none", background: "#198754" }}
            labelText={""}
            icon="bi bi-pencil"
            onButtonClick={() => {
              const id = rowData.id;
              navigate(`/document-of-output-fast/${id}`);
            }}
            disabled={activeUser?.role?.name === RoleType.Director}
          />
        )}
        {(activeUser?.role?.name === RoleType.Superadmin ||
          activeUser?.role?.name === RoleType.Director) && (
          <ButtonInput
            addStyle={{ border: "none", background: "#198754" }}
            labelText={""}
            icon="bi bi-trash"
            onButtonClick={async () => {
              const id = rowData.id;
              setOutputToDelete(id);
              setDeleteConfirmVisible(true);
            }}
            disabled={activeUser?.role?.name === RoleType.Director}
          />
        )}
      </div>
    );
  };

  const formatTransportId = (rowData: DocumentShort) => {
    return (
      <div className="invoice-container">
        <Button
          id="buttonInputWithIconId"
          label={`${
            rowData?.shipperInvoiceId === null ||
            rowData?.shipperInvoiceId === ""
              ? "unesite"
              : rowData?.shipperInvoiceId
          }`}
          onClick={() => {
            getOneDocument(rowData.id).then((resp: any) => {
              const oneOutput = resp?.data?.result ?? resp?.data;
              if (oneOutput?.finishedDocument === true) {
                setSelectedMercantileOutput(cloneDeep(oneOutput));
                setShowForm(v4());
              } else {
                //@ts-ignore
                toast.current.show({
                  severity: "error",
                  detail: t(
                    "Nije moguće uneti broj računa transporta za otpremnice koje nisu zaključene",
                  ),
                  life: 3000,
                });
              }
            });
          }}
          style={{
            border: "1px solid #ccc",
            backgroundColor: "#eee",
            color: "#000",
          }}
        />
      </div>
    );
  };
  console.log(filteredOutputDocuments);
  //TODO: ako su filteri nisu postavljani ili su resetovani, uraditi export u excel/pdf i print iz allMercantileOutputs,
  //ako nisu postavljeni, onda filteredOutputDocuments ucitava prethodnu verziju podataka
  const exportToExcel = () => {
    // Sortiranje po datumu (najstariji prvi) i po vremenu ako je isti datum
    const sortedDocuments = [...filteredOutputDocuments].sort((a, b) => {
      const dateA = new Date(a.date).getTime();
      const dateB = new Date(b.date).getTime();
      // Ako su datumi različiti, sortiraj po datumu
      if (dateA !== dateB) {
        return dateA - dateB; // Od najstarijeg ka najnovijem
      }

      // Ako su datumi isti, sortiraj po vremenu
      const timeA = a.time ? new Date(a.time).getTime() : 0;
      const timeB = b.time ? new Date(b.time).getTime() : 0;
      return timeA - timeB; // Od ranijeg ka kasnijem
    });

    const excelMercantileObject = sortedDocuments.map((item: DocumentShort) => {
      let categoryType: MercantileType = MercantileType.Wheat;
      if (item?.categoryName === "Uljana repica")
        categoryType = MercantileType.OiledBeet;
      if (item?.categoryName === "Ječam") categoryType = MercantileType.Barley;
      if (item?.categoryName === "Soja") categoryType = MercantileType.Soya;
      if (item?.categoryName === "Suncokret")
        categoryType = MercantileType.Sunflower;
      if (item?.categoryName === "Kukuruz") categoryType = MercantileType.Corn;

      const itemSrpsCalculation = srpsCalculation(
        categoryType,
        item.brutto !== undefined && item.tara !== undefined
          ? item.brutto - item.tara
          : 0,
        item?.moisture ?? 0,
        item.impurity ?? 0,
        item.breakage ?? 0,
        item.defect ?? 0,
        item.hectoliter as number,
        item.calculationMethod as boolean,
        settings,
      );

      return {
        BrojDokumenta: item.finishedDocumentNumber || item.finishedDocumentId,
        Datum: Utility.formatDate(new Date(item.date as Date)),
        Vreme: Utility.formatTime(new Date(item.time as Date)),
        MestoUtovara: item.warehouseName,
        MestoIstovara: item.loadingCityName,
        BrojCelije: item.cellNumberFloor,
        Kupac: item.partnerName,
        Roba: item.productName,
        Vozac: item.driverName,
        BrojRegistracije: item.vehicleNumber,
        Bruto: item.brutto,
        Tara: item.tara,
        Neto: item.netto,
        Vlaga: parseFloat((item.moisture ?? 0)?.toString()),
        VlagaOdbitakKg: Math.round(itemSrpsCalculation[2]),
        Primese: parseFloat((item.impurity ?? 0)?.toString()),
        PrimeseOdbitakKg: Math.round(itemSrpsCalculation[4]),
        Defekt: parseFloat((item.defect ?? 0)?.toString()),
        DefektOdbitakKg: Math.round(itemSrpsCalculation[10]),
        Lom: parseFloat((item.breakage ?? 0)?.toString()),
        LomOdbitakKg: Math.round(itemSrpsCalculation[8]),
        Hektolitar: parseFloat((item.hectoliter ?? 0)?.toString()),
        HektOdbitakKg: Math.round(itemSrpsCalculation[6]),
        Protein: parseFloat((item.protein ?? 0)?.toString()),
        // Kalo: Math.round(srpsCalculation(
        //     categoryType,
        //     (item.brutto !== undefined && item.tara !== undefined) ? item.brutto - item.tara : 0,
        //     item?.moisture ?? 0,
        //     item.impurity ?? 0,
        //     item.breakage ?? 0,
        //     item.defect ?? 0,
        //     item.hectoliter as number,
        //     item.calculationMethod as boolean,
        //     settings
        //     )[12]),
        Srps: item.srps,
        BrPrijPrem: item.documentPairId,
        PrijemKolicina: "",
        VlagaPrijem: "",
        LomPrijem: "",
        DefektPrijem: "",
        PrimesePrijem: "",
        SrpsPrijem: "",
        BrojRacunaTransporta: item.shipperInvoiceId,
        BrojRacunaKontrolneKuce: item.controlHouseInvoiceId,
        Razlika: "",
        CenaPrevoza: "",
      };
    });
    const excelObject = [
      {
        BrojDokumenta: "",
        Datum: "",
        Vreme: "",
        MestoUtovara: "",
        MestoIstovara: "",
        BrojCelije: "",
        Kupac: "",
        Roba: "",
        Vozac: "",
        BrojRegistracije: "",
        Bruto: "",
        Tara: "",
        Neto: "",
        Vlaga: "",
        VlagaOdbitakKg: "",
        Primese: "",
        PrimeseOdbitakKg: "",
        Defekt: "",
        DefektOdbitakKg: "",
        Lom: "",
        LomOdbitakKg: "",
        Hektolitar: "",
        HektOdbitakKg: "",
        Protein: "",
        //Kalo: '',
        Srps: "",
        BrPrijPrem: "",
        PrijemKolicina: "",
        VlagaPrijem: "",
        LomPrijem: "",
        DefektPrijem: "",
        PrimesePrijem: "",
        SrpsPrijem: "",
        BrojRacunaTransporta: "",
        BrojRacunaKontrolneKuce: "",
        Razlika: "",
        CenaPrevoza: "",
      },
      ...excelMercantileObject,
    ];
    import("xlsx").then((xlsx) => {
      const worksheet = xlsx.utils.json_to_sheet(excelObject);
      const workbook = { Sheets: { data: worksheet }, SheetNames: ["data"] };
      const excelBuffer = xlsx.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });
      saveAsExcelFile(
        excelBuffer,
        `Izlaz merkantile ${Utility.formatDate(new Date())}`,
      );
    });
  };

  const saveAsExcelFile = (buffer: any, fileName: any) => {
    import("file-saver").then((module: any) => {
      if (module && module.default) {
        let EXCEL_TYPE =
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
        let EXCEL_EXTENSION = ".xlsx";
        const data = new Blob([buffer], {
          type: EXCEL_TYPE,
        });
        module.default.saveAs(data, fileName + EXCEL_EXTENSION);
      }
    });
  };

  return (
    <PageLayout>
      <Toast ref={toast} position={"top-right"} />
      <div className="table-wrapper">
        <div className="table-header">
          <div className="header-left">
            <i
              className="bi bi-boxes header-title-icon"
              style={{
                fontSize: 14,
                marginLeft: "1rem",
              }}
            ></i>
            <span className="header-left-title">{t("Izlaz merkantile")}</span>
            <span className="header-elements-text">{`${t("od")}`}</span>
            <DateInput
              id="dateFrom"
              value={dateFrom}
              onValueChange={onDateFromChange}
              minDate={yearStart}
              maxDate={yearEnd}
              labelText=""
              placeholderText=""
              icon="bi bi-calendar-date"
              control={control}
              errors={errors}
              disabled={processing}
            />
            <span className="header-elements-text">{`${t("do")}`}</span>
            <DateInput
              id="dateTo"
              value={dateTo}
              onValueChange={onDateToChange}
              minDate={yearStart}
              maxDate={yearEnd}
              labelText=""
              placeholderText=""
              icon="bi bi-calendar-date"
              control={control}
              errors={errors}
              disabled={processing}
            />
            <span className="header-elements-text">{`${t("iz")}`}</span>
            <DropdownInput
              id="warehouse"
              value={selectedWarehouse?.id}
              onValueChange={handleWarehouseChange}
              optionValue="id"
              optionLabel="name"
              options={warehouses.filter((item) => item.virtual !== true)}
              labelText=""
              placeholderText={t("Magacin")}
              control={control}
              setValue={setValue}
              errors={errors}
              disabled={
                activeUser?.role?.name === RoleType.Warehouseman || processing
              }
            />
            <span className="header-elements-text">{`${t("za")}`}</span>
            <DropdownInput
              id="productCategory"
              value={selectedProductCategory?.id}
              onValueChange={handleProductCategoryChange}
              optionValue="id"
              optionLabel="name"
              options={productCategories.filter(
                (item) => item.type == ProductType.MercantileGoods,
              )}
              filter={false}
              labelText=""
              placeholderText={t("vrstu robe")}
              control={control}
              setValue={setValue}
              errors={errors}
              rules={{ required: true }}
              disabled={processing}
            />
            <span className="header-elements"> </span>
            <DropdownInput
              id="selectedProduct"
              value={selectedProduct?.id}
              onValueChange={handleProductChange}
              optionValue="id"
              optionLabel="name"
              options={products.filter(
                (item) => item.category?.id === selectedProductCategory?.id,
              )}
              filter={false}
              labelText=""
              placeholderText={t("Ime")}
              control={control}
              setValue={setValue}
              errors={errors}
              rules={{ required: true }}
              disabled={selectedProductCategory === undefined}
            />
            <ButtonInput
              addStyle={{
                border: "none",
                background: "#ffffff",
                color: "#6C757D",
                margin: "0 0 0 1rem",
              }}
              labelText={"reset"}
              icon="bi bi-arrow-repeat"
              onButtonClick={() => {
                const { from, to } = getDefaultRangeForBusinessYear();
                // reset local states
                if (activeUser?.role?.name !== RoleType.Warehouseman) {
                  setSelectedWarehouse({} as Warehouse);
                  selectedWarehouseSet(undefined);
                }
                setDateFrom(from);
                setDateTo(to);
                setSelectedProductCategory(undefined);
                setSelectedProduct({} as Product);
                // reset global state filtera
                selectedDateFromSet(from);
                selectedDateToSet(to);
                selectedProductCategorySet(undefined);
                selectedProductSet(undefined);
              }}
              disabled={processing}
            />
          </div>
          <div className="header-right">
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
                margin: "0 .5rem",
              }}
              labelText={""}
              icon="bi bi-file-earmark-pdf"
              onButtonClick={() => {
                if (downloadPdfDocument.current) {
                  // @ts-ignore
                  downloadPdfDocument.current();
                }
              }}
              disabled={processing === true}
            />
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
              }}
              labelText={""}
              icon="bi bi-printer"
              onButtonClick={() => {
                if (printPdfDocument.current) {
                  // @ts-ignore
                  printPdfDocument.current();
                }
              }}
              disabled={processing === true}
            />
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
                marginLeft: ".5rem",
                marginRight: ".5rem",
              }}
              labelText={""}
              icon="bi bi-file-earmark-excel"
              onButtonClick={exportToExcel}
              disabled={processing === true}
            />
          </div>
        </div>
        {processing ? (
          <ProgressSpinner />
        ) : (
          <div className="table-body">
            <DataTable
              scrollable={true}
              scrollHeight="80vh"
              sortMode="single"
              removableSort
              filters={filters}
              filterDisplay="row"
              filterDelay={300}
              globalFilterFields={[
                "finishedDocumentNumber",
                "documentPairId",
                "loadingCityName",
                "partnerName",
                "driverName",
                "vehicleNumber",
                "brutto",
                "tara",
                "srps",
                "netto",
                "breakage",
                "defect",
                "hectoliter",
              ]}
              header={renderHeader()}
              emptyMessage="Nema otpremnica merkantile za zadate kriterijume"
              value={mercantileOutputDocuments}
              onFilter={(e) => {
                debouncedSetFilters(cloneDeep(e.filters));
              }}
              onValueChange={(filteredData) => {
                debouncedSetFilteredOutputDocuments(filteredData);
              }}
              showGridlines
              stripedRows
            >
              <Column
                frozen
                body={formatFinishedDocumentId}
                field="finishedDocumentNumber"
                filterField={"finishedDocumentNumber"}
                filter
                header={t("Br.Dok.")}
                style={{ minWidth: "100px" }}
                footer=""
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
              />
              <Column
                field="documentPairId"
                filter
                header={t("Br.prij.prem.")}
                style={{ minWidth: "100px" }}
                footer=""
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
              />
              <Column
                body={formatStatus}
                filter
                filterField={"status"}
                showFilterMenu={false}
                filterElement={statusRowFilterTemplate}
                header={t("Status")}
                style={{ minWidth: "150px" }}
                footer=""
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
              />
              <Column
                body={formatDate}
                header={t("Datum")}
                style={{ minWidth: "80px" }}
                footer=""
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
              />
              <Column
                field="warehouseName"
                filter
                header={t("Mesto utovara")}
                style={{ minWidth: "90px" }}
                footer=""
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
              />
              <Column
                field="loadingCityName"
                header={t("Mesto istovara")}
                style={{ minWidth: "90px" }}
                footer=""
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
              />
              <Column
                field="partnerName"
                sortable
                filter
                header={t("Kupac")}
                style={{ minWidth: "100px" }}
                footer=""
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
              />
              <Column
                body={formatProductName}
                header={t("Naziv robe")}
                style={{ minWidth: "100px" }}
                footer=""
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
              />
              <Column
                field="driverName"
                filter
                header={t("Vozač")}
                style={{ minWidth: "80px" }}
                footer=""
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
              />
              <Column
                field="vehicleNumber"
                filter
                header={t("Broj registracije")}
                style={{ minWidth: "80px" }}
                footer=""
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
              />
              <Column
                field="moisture"
                filter
                sortable
                header={t("Vlaga")}
                style={{ minWidth: "70px" }}
                footer=""
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
              />
              <Column
                field="impurity"
                filter
                sortable
                header={t("Primese")}
                style={{ minWidth: "70px" }}
                footer=""
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
              />
              <Column
                field="breakage"
                sortable
                filter
                header={t("Lom")}
                style={{ minWidth: "70px" }}
                footer=""
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
              />
              <Column
                field="defect"
                sortable
                filter
                header={t("Defekt")}
                style={{ minWidth: "70px" }}
                footer=""
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
              />
              <Column
                field="hectoliter"
                sortable
                filter
                header={t("Hektolitar")}
                style={{ minWidth: "70px" }}
                footer={t("Ukupno:")}
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
              />
              {activeUser?.role?.name === RoleType.Warehouseman && (
                <Column
                  field="brutto"
                  filter
                  header={t("Bruto")}
                  style={{ minWidth: "70px" }}
                  footer=""
                  footerStyle={{
                    backgroundColor: "#6C757D",
                    color: "#FFFFFF",
                    border: "none",
                  }}
                />
              )}
              {activeUser?.role?.name === RoleType.Warehouseman && (
                <Column
                  field="tara"
                  filter
                  header={t("Tara")}
                  style={{ minWidth: "70px" }}
                  footer=""
                  footerStyle={{
                    backgroundColor: "#6C757D",
                    color: "#FFFFFF",
                    border: "none",
                  }}
                />
              )}
              <Column
                body={amountNetto}
                filterField={"netto"}
                filter
                header={t("Neto")}
                style={{ minWidth: "80px" }}
                footer={Utility.addThousandSeparator(nettoSum) + " kg"}
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
              />
              <Column
                filterField={"srps"}
                filter
                body={amountSrps}
                header={t("Srps")}
                style={{ minWidth: "80px" }}
                footer={Utility.addThousandSeparator(srpsSum) + " kg"}
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
              />
              {activeUser?.role?.name !== RoleType.Warehouseman && (
                <Column
                  body={formatTransportId}
                  header={t("Br.rač.tr.")}
                  style={{ minWidth: "100px" }}
                  footer=""
                  footerStyle={{
                    backgroundColor: "#6C757D",
                    color: "#FFFFFF",
                    border: "none",
                  }}
                />
              )}
              {activeUser?.role?.name !== RoleType.Warehouseman && (
                <Column
                  body={formatControlHouseId}
                  header={t("Br.rač.kon.")}
                  style={{ minWidth: "100px" }}
                  footer=""
                  footerStyle={{
                    backgroundColor: "#6C757D",
                    color: "#FFFFFF",
                    border: "none",
                  }}
                />
              )}
              <Column
                body={formatOptions}
                header={t("Opcije")}
                style={{ minWidth: "150px" }}
                footer=""
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
              />
            </DataTable>
          </div>
        )}
        <div className="document-preview">
          <PdfPreview
            pdfDefinition={pdfData}
            downloadDocument={downloadPdfDocument}
            printDocument={printPdfDocument}
            documentName={`${t("Izlaz merkantile")}_${Utility.formatDate(
              new Date(),
            )}.pdf`}
          />
        </div>
      </div>
      <ConfirmDialog
        visible={deleteConfirmVisible}
        onHide={() => setDeleteConfirmVisible(false)}
        message={t("Da li ste sigurni da želite da izbrišete otpremnicu?")}
        header={t("Potvrda brisanja otpremnice")}
        icon="bi bi-exclamation-circle"
        acceptLabel={`${t("Da")}`}
        acceptClassName="accept-button"
        accept={accept}
        rejectLabel={`${t("Ne")}`}
        rejectClassName="reject-button"
        reject={() => {}}
      />
      <NewInvoiceIdForm
        buttonLabel={``}
        buttonIcon="bi bi-boxes"
        show={showForm}
        selectedDocument={selectedMercantileOutput}
        onSaved={refreshDocuments}
        buttonStyle={{
          border: "none",
          background: "#ffffff",
          color: "#6C757D",
          marginRight: "1rem",
        }}
        buttonSaveStyle={{ border: "none", background: "rgba(0, 165, 83, 1)" }}
        buttonResetStyle={{
          border: "none",
          background: "rgba(220, 53, 69, 1)",
        }}
      />
    </PageLayout>
  );
};
